import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { EnumPaymentType, EnumCashierTxStatus } from "@/api/cashier";
import { NavButtons } from "@/components/general/nav-buttons/NavButtons";
import { Section } from "@/components/section/Section";
import { TransactionLayout } from "@/components/transaction/transaction-layout/TransactionLayout";
import { TransactionStatus } from "@/components/transaction/transaction-status/TransactionStatus";
import { TransactionStatusSkeleton } from "@/components/transaction/transaction-status/transaction-status-skeleton/TransactionStatusSkeleton";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { useTradingAccounts } from "@/hooks/useTradingAccounts";
import { GLOBAL_NAVIGATION_URLS, globalNavigation } from "@/utils/constants";
import { ECashierFlows, ERoutes } from "@/utils/enums";
import style from "./style.module.css";
import { majorToAmountCurrencyResponse } from "@/utils/helpers";

export const TransferCompletion = () => {
  const navigate = useNavigate();
  const { getValueFromBankingAmount } = useFormatAmount();
  const { t } = useTranslation();
  const { data: accounts, isLoading: isAccountsLoading } = useTradingAccounts();
  const location = useLocation();

  const {
    accountId,
    toAccountId,
    processingValue,
    tradingValue,
    sourceAccountBalance,
    targetAccountBalance,
  } = location.state;

  const { selectedAccount, selectedToAccount } = useMemo(() => {
    if (!accounts)
      return {
        selectedAccount: null,
        selectedToAccount: null,
      };
    return {
      selectedAccount: accounts.find((account) => account.id === accountId),
      selectedToAccount: accounts.find((account) => account.id === toAccountId),
    };
  }, [accountId, toAccountId, accounts]);

  const transactionStatus: EnumCashierTxStatus = useMemo(() => {
    if (location.pathname.includes(ERoutes.CompletionSuccess)) {
      return EnumCashierTxStatus.CashierTxComplete;
    }
    if (location.pathname.includes(ERoutes.CompletionFailed)) {
      return EnumCashierTxStatus.CashierTxUnsuccessful;
    }
    return EnumCashierTxStatus.CashierTxPending;
  }, [location.pathname]);

  const hasSucceed =
    transactionStatus === EnumCashierTxStatus.CashierTxComplete;

  const handleBack = () => {
    if (hasSucceed) {
      navigate(`/${ECashierFlows.Transfer}`);
    } else {
      globalNavigation(GLOBAL_NAVIGATION_URLS.DASHBOARD, true);
    }
  };

  const handleNext = () => {
    navigate(`/${hasSucceed ? ECashierFlows.History : ECashierFlows.Transfer}`);
  };

  if (isAccountsLoading || !selectedAccount || !selectedToAccount)
    return <TransactionStatusSkeleton />;

  const fromBalance =
    getValueFromBankingAmount(
      sourceAccountBalance || selectedAccount.balance
    ) ?? 0;
  const toBalance =
    getValueFromBankingAmount(
      targetAccountBalance || selectedToAccount.balance
    ) ?? 0;

  return (
    <TransactionLayout>
      <Section className={`${style["grid-item"]}`}>
        {selectedAccount && selectedToAccount && (
          <TransactionStatus
            transactionStatus={{
              money: majorToAmountCurrencyResponse(
                processingValue,
                selectedAccount.currency
              ),
              status: transactionStatus,
              balance: majorToAmountCurrencyResponse(
                fromBalance,
                selectedAccount.currency
              ),
              external_trading_account_id: selectedAccount.id,
            }}
            balance={fromBalance}
            transactionType={EnumPaymentType.EnumPaymentTypeTransfer}
            account={{
              ...selectedAccount,
              balance: sourceAccountBalance || selectedAccount.balance,
            }}
            toAccountData={{
              account: {
                ...selectedToAccount,
                balance: targetAccountBalance || selectedToAccount.balance,
              },
              balance: +toBalance,
              transferredAmount: tradingValue,
            }}
          />
        )}
        <NavButtons
          backLabel={
            hasSucceed
              ? t("transfer.nav_buttons.make_another_transfer")
              : t("global.nav_buttons.back_to_dashboard")
          }
          nextLabel={
            hasSucceed
              ? t("global.nav_buttons.view_history")
              : t("global.nav_buttons.try_again")
          }
          backAction={handleBack}
          nextAction={handleNext}
        />
      </Section>
    </TransactionLayout>
  );
};
